// Client-side image conditioning for every store-asset upload.
//
// Two problems this solves, in the browser, before a byte leaves the page:
//   1. Our upload routes cap the body at 8 MB and store only PNG/JPEG, so a
//      phone-camera PNG or a WebP export used to come back as a 400.
//   2. A slot with a fixed geometry (Play icon 512x512, feature graphic
//      1024x500) has to receive exactly that — an operator dropping a
//      2048x2048 icon should get it resized, not rejected.
//
// The output is a File the caller can hand straight to the upload API: PNG when
// it fits, JPEG when the PNG is too heavy, downscaled further only if even JPEG
// cannot get under the cap.

import { ACCENT_PRESETS, coverSource, renderPhoneScreenshot, type AccentPreset } from './marketing-image';

/** Server routes reject above 8 MB; stay clear of the edge. */
export const UPLOAD_BYTE_LIMIT = 7.5 * 1024 * 1024;

export interface ImageTarget {
  /** Shown in error messages. */
  label: string;
  /** Fixed output geometry — the source is cover-cropped to this box. */
  exact?: { width: number; height: number };
  /**
   * Free geometry — the source is scaled to fit inside the box (never cropped,
   * never enlarged beyond it), then padded up if a side falls under `minSide`.
   */
  box?: { maxWidth: number; maxHeight: number; minSide?: number };
  /** Longest side over shortest side the slot accepts; a wider source is refused, not cropped. */
  maxAspect?: number;
  maxBytes: number;
  /** 'png' keeps the source lossless while it fits; 'jpeg' always re-encodes. */
  format: 'png' | 'jpeg';
}

// --- Slot specs -------------------------------------------------------------

/** Play listing icon: exactly 512x512. */
export const PLAY_ICON: ImageTarget = {
  label: 'app icon', exact: { width: 512, height: 512 }, maxBytes: UPLOAD_BYTE_LIMIT, format: 'png',
};

/** Play feature graphic: exactly 1024x500. */
export const PLAY_FEATURE_GRAPHIC: ImageTarget = {
  label: 'feature graphic', exact: { width: 1024, height: 500 }, maxBytes: UPLOAD_BYTE_LIMIT, format: 'png',
};

/** Play screenshots keep their aspect; every side must land in 320..3840, the long side at most twice the short one. */
export const PLAY_SCREENSHOT: ImageTarget = {
  label: 'screenshot',
  box: { maxWidth: 3840, maxHeight: 3840, minSide: 320 },
  maxAspect: 2,
  maxBytes: UPLOAD_BYTE_LIMIT,
  format: 'png',
};

/** Wizard icon candidates: square, at the 1024 the generator emits. */
export const WIZARD_ICON: ImageTarget = {
  label: 'icon', exact: { width: 1024, height: 1024 }, maxBytes: UPLOAD_BYTE_LIMIT, format: 'png',
};

/** Wizard feature banner, drawn or uploaded at 1024x500. */
export const WIZARD_FEATURE_BANNER: ImageTarget = { ...PLAY_FEATURE_GRAPHIC, label: 'feature banner' };

/** Wizard frames and build-repo frames end up as store screenshots. */
export const STORE_SCREENSHOT: ImageTarget = PLAY_SCREENSHOT;

/** Handoff store assets — free geometry, just has to be PNG/JPEG under the cap. */
export const STORE_ASSET: ImageTarget = {
  label: 'store asset',
  box: { maxWidth: 3840, maxHeight: 3840 },
  maxBytes: UPLOAD_BYTE_LIMIT,
  format: 'png',
};

// --- Geometry (pure) --------------------------------------------------------

/** Output pixel size for a source of `srcW`x`srcH` in this slot. */
export function outputSize(srcW: number, srcH: number, target: ImageTarget): { width: number; height: number } {
  if (target.exact) return { ...target.exact };
  const box = target.box ?? { maxWidth: srcW, maxHeight: srcH };
  const w = Math.max(1, srcW);
  const h = Math.max(1, srcH);
  let scale = Math.min(1, box.maxWidth / w, box.maxHeight / h);
  const minSide = box.minSide ?? 0;
  if (minSide > 0 && Math.min(w, h) * scale < minSide) {
    // Too small for the store: scale up to the floor, but never past the box.
    scale = Math.min(minSide / Math.min(w, h), box.maxWidth / w, box.maxHeight / h);
  }
  return {
    width: Math.max(1, Math.round(w * scale)),
    height: Math.max(1, Math.round(h * scale)),
  };
}

/** A readable image in the wrong slot — its shape, not its file, is the problem. */
export class AspectMisfit extends Error {
  constructor(message: string, readonly width: number, readonly height: number) { super(message); }
  /** Shaped like the 1024×500 feature graphic, not like a phone capture longer than 2:1. */
  get bannerShaped(): boolean { return this.width > this.height && this.width / this.height < 2.1; }
}

/**
 * Why a `srcW`x`srcH` source does not fit the slot's aspect limit, or null.
 * A 1024×500 feature banner dropped among screenshots is the usual case: Play
 * refuses it there, and it belongs in the feature graphic slot.
 */
export function aspectError(srcW: number, srcH: number, target: ImageTarget): string | null {
  if (!target.maxAspect) return null;
  const long = Math.max(srcW, srcH);
  const short = Math.max(1, Math.min(srcW, srcH));
  if (long / short <= target.maxAspect) return null;
  const banner = srcW > srcH ? ' A feature banner goes into the feature graphic slot.' : '';
  return `This ${target.label} is ${srcW}×${srcH}, longer than ${target.maxAspect}:1 — Play does not accept it as a ${target.label}.${banner}`;
}

/**
 * True when the file can be uploaded untouched: right type, already the exact
 * pixel size the slot wants, and inside the byte cap. Re-encoding a good PNG
 * only costs quality, so it is worth checking.
 */
export function canPassThrough(
  type: string, bytes: number, srcW: number, srcH: number, target: ImageTarget,
): boolean {
  if (type !== 'image/png' && type !== 'image/jpeg') return false;
  if (bytes > target.maxBytes) return false;
  const out = outputSize(srcW, srcH, target);
  return out.width === srcW && out.height === srcH;
}

/** Quality ladder tried before giving up on the pixel size. */
const JPEG_QUALITIES = [0.92, 0.85, 0.75, 0.65, 0.55];
/** How many times the pixel size may be cut when even the lowest quality is too big. */
const MAX_DOWNSCALE_ROUNDS = 4;
const DOWNSCALE_STEP = 0.75;

// --- Encoding ---------------------------------------------------------------

interface Decoded {
  source: CanvasImageSource;
  width: number;
  height: number;
  release: () => void;
}

async function decode(blob: Blob): Promise<Decoded> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(blob);
      return { source: bitmap, width: bitmap.width, height: bitmap.height, release: () => bitmap.close() };
    } catch {
      // Fall through: some browsers refuse odd sources that <img> still decodes.
    }
  }
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('decode failed'));
      el.src = url;
    });
    return {
      source: img,
      width: img.naturalWidth || img.width,
      height: img.naturalHeight || img.height,
      release: () => URL.revokeObjectURL(url),
    };
  } catch (cause) {
    URL.revokeObjectURL(url);
    throw cause;
  }
}

function draw(decoded: Decoded, width: number, height: number, exact: boolean): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  if (exact) {
    // Fixed slot: fill it edge to edge, cropping the overflowing axis.
    const { sx, sy, sw, sh } = coverSource(decoded.width, decoded.height, width, height);
    ctx.drawImage(decoded.source, sx, sy, sw, sh, 0, 0, width, height);
  } else {
    ctx.drawImage(decoded.source, 0, 0, width, height);
  }
  return canvas;
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Image encoding failed'))),
      type,
      quality,
    );
  });
}

function renamed(name: string, type: string): string {
  const base = name.replace(/\.[^.]+$/, '') || 'image';
  return `${base}.${type === 'image/jpeg' ? 'jpg' : 'png'}`;
}

/**
 * Resize `input` to what `target` expects and compress it under the byte cap.
 * Returns the original file untouched when it already qualifies.
 */
export async function prepareImageUpload(
  input: Blob, target: ImageTarget, filename?: string,
): Promise<File> {
  const name = filename ?? (input instanceof File ? input.name : 'image.png');

  let decoded: Decoded;
  try {
    decoded = await decode(input);
  } catch {
    throw new Error(`Could not read this ${target.label} — use a PNG or JPEG image.`);
  }

  try {
    const misfit = aspectError(decoded.width, decoded.height, target);
    if (misfit) throw new AspectMisfit(misfit, decoded.width, decoded.height);
    if (canPassThrough(input.type, input.size, decoded.width, decoded.height, target)) {
      return input instanceof File ? input : new File([input], name, { type: input.type });
    }

    let { width, height } = outputSize(decoded.width, decoded.height, target);
    for (let round = 0; round <= MAX_DOWNSCALE_ROUNDS; round++) {
      const canvas = draw(decoded, width, height, !!target.exact);
      if (target.format === 'png') {
        const png = await toBlob(canvas, 'image/png');
        if (png.size <= target.maxBytes) {
          return new File([png], renamed(name, 'image/png'), { type: 'image/png' });
        }
      }
      for (const quality of JPEG_QUALITIES) {
        const jpeg = await toBlob(canvas, 'image/jpeg', quality);
        if (jpeg.size <= target.maxBytes) {
          return new File([jpeg], renamed(name, 'image/jpeg'), { type: 'image/jpeg' });
        }
      }
      // Nothing fit at this size. A fixed slot cannot shrink, so stop.
      if (target.exact || round === MAX_DOWNSCALE_ROUNDS) break;
      width = Math.max(1, Math.round(width * DOWNSCALE_STEP));
      height = Math.max(1, Math.round(height * DOWNSCALE_STEP));
    }
    throw new Error(`This ${target.label} is too heavy to compress under the upload limit.`);
  } finally {
    decoded.release();
  }
}

/**
 * A store screenshot. A phone capture longer than Play's 2:1 (an iPhone's
 * 19.5:9) is put into a phone frame on `preset` instead of being refused or
 * cut; a banner-shaped image is still refused with its AspectMisfit.
 */
export async function prepareScreenshotUpload(input: File, preset: AccentPreset = ACCENT_PRESETS[0]): Promise<File> {
  try {
    return await prepareImageUpload(input, STORE_SCREENSHOT);
  } catch (cause) {
    if (!(cause instanceof AspectMisfit) || cause.bannerShaped) throw cause;
  }
  const decoded = await decode(input);
  try {
    const framed = await toBlob(renderPhoneScreenshot(decoded.source, { preset }), 'image/png');
    return await prepareImageUpload(framed, STORE_SCREENSHOT, renamed(input.name, 'image/png'));
  } finally {
    decoded.release();
  }
}

/** `prepareImageUpload` over a list, keeping order. Rejects on the first failure. */
export async function prepareImageUploads(files: File[], target: ImageTarget): Promise<File[]> {
  const out: File[] = [];
  for (const file of files) out.push(await prepareImageUpload(file, target));
  return out;
}
